import { OfferLinePrice, OrderResponse } from "../../types";

/**
 * SINGLE SOURCE OF TRUTH for the `POST /offer` (single) + `POST /offer/multiple`
 * / `add-offers` (joint) create payload. Extracted VERBATIM from the pre-Carbon
 * `Offer.tsx#handleSubmitOffers`'s own inline `buildOfferPayload` closure so the
 * request body stays BYTE-EQUIVALENT through the Carbon migration (Task 5,
 * ruling 1). The container decides *which* mutation to call (single vs. joint
 * vs. add-to-existing-joint) and which lines to include; this function only
 * shapes ONE order line's offer body.
 *
 * The joint path (`createMultipleOffer` / `addOffersToJointOrder`) submits an
 * array of these bodies — one per non-declined, non-existing, complete line —
 * built by mapping this function over the selected orders, exactly as the
 * pre-Carbon container did.
 */

/** The offer-level (per-material) data collected by `MaterialOfferAccordion`. */
export interface MaterialOfferData {
  price: string | number;
  currency: string;
  comment?: string;
  expireDate?: string;
  /** SCRUM-116: on a service quoted by line, one price per line; `price` is their total. */
  linePrices?: OfferLinePrice[];
}

/** The offer-wide "general conditions" collected once for the whole submission. */
export interface OfferGeneralConditionsData {
  paymentMethod: string;
  paymentTerm: string;
  leadTime: string;
  offerValidity: string;
  generalComments: string;
  deliveryToBuyer: boolean;
  deliveryCostIncluded: boolean;
  additionalDeliveryCost: string;
  deliveryCostCurrency: string;
  pickupAddress?: string;
}

export interface BuildOfferPayloadArgs {
  orderItem: OrderResponse;
  offer: MaterialOfferData;
  generalConditions: OfferGeneralConditionsData;
  user: { id: string };
  hasServiceMaterial: boolean;
  uploadedFileUrl: string | undefined | null;
  /** The order is picked up by the buyer (SCRUM-70): only a pickup address, no logistics. */
  buyerPicksUp?: boolean;
}

/**
 * The seller charges a logistics cost only when it delivers to the buyer and
 * the cost is not already in the price (SCRUM-36). Not delivering means the
 * buyer handles logistics (`deliveryPlace: "BUYER"`).
 */
const chargedDeliveryCost = (
  generalConditions: OfferGeneralConditionsData,
  hasServiceMaterial: boolean,
  buyerPicksUp = false
) =>
  !hasServiceMaterial &&
  !buyerPicksUp &&
  generalConditions.deliveryToBuyer &&
  !generalConditions.deliveryCostIncluded &&
  generalConditions.additionalDeliveryCost
    ? parseFloat(String(generalConditions.additionalDeliveryCost))
    : 0;

export const buildOfferPayload = ({
  orderItem,
  offer,
  generalConditions,
  user,
  hasServiceMaterial,
  uploadedFileUrl,
  buyerPicksUp = false,
}: BuildOfferPayloadArgs) => ({
  order: { id: orderItem.id },
  price: parseFloat(String(offer.price)),
  currency: offer.currency,
  comment: offer.comment || "",
  generalComments: generalConditions.generalComments,
  user: { id: user.id },
  initialPrice: orderItem.initialPrice || null,
  oldPrice: parseFloat(String(offer.price)),
  deliveryCost: chargedDeliveryCost(
    generalConditions,
    hasServiceMaterial,
    buyerPicksUp
  ),
  deliveryCostCurrency:
    chargedDeliveryCost(generalConditions, hasServiceMaterial, buyerPicksUp) >
    0
      ? generalConditions.deliveryCostCurrency
      : null,
  deliveryPlace:
    !hasServiceMaterial && !buyerPicksUp && generalConditions.deliveryToBuyer
      ? "SELLER"
      : "BUYER",
  ...(buyerPicksUp
    ? { pickupAddress: generalConditions.pickupAddress?.trim() ?? "" }
    : {}),
  selected: false,
  offerValidity: generalConditions.offerValidity,
  leadTime: generalConditions.leadTime,
  expireDate: offer.expireDate || generalConditions.offerValidity,
  paymentTerm: generalConditions.paymentTerm,
  paymentMethod: generalConditions.paymentMethod,
  fileURL: uploadedFileUrl ?? "",
  ...(offer.linePrices?.length ? { linePrices: offer.linePrices } : {}),
});
